import React, { useEffect, useMemo, useRef, useState } from 'react'
import PhonePrefixSelect from './PhonePrefixSelect'
import { countryPhones } from './constants'
import { useTranslation } from 'react-i18next'

const Wrapper: React.FC<{ height?: number; children: React.ReactNode }> = ({ height = 44, children }) => (
    <div
        style={{
            display: 'flex',
            width: '100%',
            height,
        }}
    >
        {children}
    </div>
)

const StyledInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => (
    <input
        ref={ref}
        maxLength={16}
        style={{
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '20px',
            flexGrow: 1,
            width: '100%',
            border: '1px solid #CBCBCD',
            borderLeft: 'none',
            borderTopRightRadius: 6,
            borderBottomRightRadius: 6,
            outline: 'none',
            ...props.style,
        }}
        {...props}
    />
))

type PhoneInputProps = {
    onChange?: (value: { prefix: string; number: string }) => void
    prefix?: string | null
    number?: string | null
    height?: number
    initialInputValue?: string
    slotStyles?: {
        prefix?: React.CSSProperties
        number?: React.CSSProperties
    }
}

const PhoneInput: React.FC<PhoneInputProps> = ({
    onChange,
    prefix = '',
    number = '',
    slotStyles,
    height,
    initialInputValue,
}) => {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const [phonePrefix, setPrefix] = useState<string>(prefix ?? '')
    const [phoneNumber, setPhoneNumber] = useState<string>(
        initialInputValue ? initialInputValue : ((number || prefix) ?? '')
    )
    // Map for fast prefix lookup
    const prefixMap = useMemo(() => {
        const map = new Map<string, string>()
        for (const entry of countryPhones) {
            map.set(entry.prefix, entry.prefix)
        }
        return map
    }, [])

    const handlePrefixChange = (newPrefix: string) => {
        console.log('handlePrefixChange called, newPrefix:', newPrefix)
        setPrefix(newPrefix)
        setPhoneNumber(newPrefix)
        onChange?.({ prefix: newPrefix, number: '' })
        setTimeout(() => {
            // іноді після setState варто глянути, що реально оновилось
            console.log('After setPrefix/setPhoneNumber: phonePrefix', phonePrefix, 'phoneNumber', phoneNumber)
        }, 0)
        inputRef.current?.focus()
    }

    const isValidPhoneInput = (value: string) => /^[+\d]*$/.test(value)

    const preparePhoneUpdate = (value: string, currentPrefix: string) => {
        const numberPart = value.replace(currentPrefix, '')
        return {
            prefix: currentPrefix,
            number: numberPart.startsWith('+') ? '' : numberPart,
        }
    }

    const extractPrefixFromInput = (value: string): string | undefined => {
        if (!value.startsWith('+')) return undefined

        // Try to find the longest matching prefix at the start of the input
        let longestMatch: string | undefined
        for (const [prefix] of prefixMap) {
            if (value.startsWith(prefix)) {
                if (!longestMatch || prefix.length > longestMatch.length) {
                    longestMatch = prefix
                }
            }
        }

        return longestMatch
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (!isValidPhoneInput(value)) return
        if (/^\d/.test(value)) {
            value = `+${value}`
        }
        const matchedPrefix = extractPrefixFromInput(value)
        const newPrefix = matchedPrefix || phonePrefix
        setPrefix(matchedPrefix ?? '')
        setPhoneNumber(value)

        console.log('handlePhoneChange', { value, matchedPrefix, newPrefix })
        onChange?.(preparePhoneUpdate(value, newPrefix))
    }

    console.log('RENDER phonePrefix:', phonePrefix, 'phoneNumber:', phoneNumber)

    useEffect(() => {
        if (prefix) {
            setPrefix((prev) => {
                if (prev === '' && prev !== prefix) {
                    return prefix ?? ''
                }
                return prev
            })
        }
        if (number) {
            setPhoneNumber((prev) => {
                if (prev === '' && prev !== number) {
                    return number ?? ''
                }
                return prev
            })
        }
    }, [prefix, number])

    return (
        <Wrapper height={height}>
            <PhonePrefixSelect
                initialPhonePrefix={phonePrefix}
                value={phonePrefix}
                onChange={handlePrefixChange}
                styles={slotStyles?.prefix}
            />
            <StyledInput
                ref={inputRef}
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder={'+1 234 567 89 00'}
                style={{ ...slotStyles?.number }}
            />
        </Wrapper>
    )
}

export default PhoneInput
